import React, { useState, useEffect } from 'react';
import { Home, Image as ImageIcon, Zap, Clock, ArrowRight, Sparkles } from 'lucide-react';
import { type AppView } from '../src/routes/slugs';
import { UserProfile } from '../types';
import { Property } from './PropertyManager';
import { OnboardingChecklist } from './OnboardingChecklist';
import { StartStagingModal } from './StartStagingModal';
import { api } from '../services/api';

interface Props {
  user: UserProfile;
  onNavigateProperties: () => void;
  onNavigateSubscription: () => void;
  onNavigate?: (view: AppView) => void;
  onSelectProperty?: (property: Property) => void;
  /**
   * STA-198: pick handler voor de Start staging modal.
   * Routet naar PropertyManager + opent direct het SmartUpload-paneel zodat
   * meerdere foto's tegelijk kunnen worden geüpload (batch flow).
   */
  onStartStaging?: (property: Property) => void;
  onCreditsUpdate?: (credits: number) => void;
}

const timeAgo = (dt: string | null): string => {
  if (!dt) return 'Nog geen renders';
  const diff = Date.now() - new Date(dt).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Zojuist';
  if (mins < 60) return `${mins} min geleden`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} uur geleden`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} dagen geleden`;
  return new Date(dt).toLocaleDateString('nl-NL', { day: '2-digit', month: 'short' });
};

const greeting = (): string => {
  const h = new Date().getHours();
  if (h < 6) return 'Goedenacht';
  if (h < 12) return 'Goedemorgen';
  if (h < 18) return 'Goedemiddag';
  return 'Goedenavond';
};

export const OverviewPage: React.FC<Props> = ({ user, onNavigateProperties, onNavigateSubscription, onNavigate, onSelectProperty, onStartStaging, onCreditsUpdate }) => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [startStagingOpen, setStartStagingOpen] = useState(false);

  useEffect(() => {
    api.get<Property[]>('/api/properties')
      .then(data => setProperties(Array.isArray(data) ? data : []))
      .catch(() => setProperties([]))
      .finally(() => setLoading(false));
  }, []);

  const totalRenders = properties.reduce((sum, p) => sum + (Number(p.render_count) || 0), 0);
  const propertyCount = properties.length;
  const lastRenderAt = properties
    .map(p => p.last_render_at)
    .filter((d): d is string => !!d)
    .sort()
    .pop() || null;

  const recentProperties = [...properties]
    .filter(p => p.last_render_at)
    .sort((a, b) => (b.last_render_at || '').localeCompare(a.last_render_at || ''))
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-bg">
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">

        {/* Welkomstheader */}
        <div className="mb-6">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink/45 mb-2">Overzicht</p>
          <h1 className="font-sans text-3xl md:text-4xl font-bold tracking-[-0.02em] text-ink">
            {/* P1-04: guard against empty firstName — avoids "Goedemorgen," with trailing comma */}
            {greeting()}{user.firstName ? `, ${user.firstName}` : '!'}
          </h1>
          <p className="text-sm text-ink/55 mt-2">Een snel overzicht van je account en recente activiteit.</p>
        </div>

        {/* STA-198: hero CTA — Start staging vanaf overzicht */}
        <button
          type="button"
          onClick={() => setStartStagingOpen(true)}
          className="w-full mb-8 rounded-2xl bg-gradient-to-br from-accent to-accent-hover p-6 md:p-7 text-left shadow-[0_8px_24px_-8px_rgba(241,110,0,0.5)] hover:shadow-[0_12px_28px_-8px_rgba(241,110,0,0.55)] transition-shadow focus:outline-none focus:ring-4 focus:ring-accent/30"
        >
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="inline-flex items-center gap-2 text-white/85 text-[11px] font-semibold uppercase tracking-[0.12em] mb-1.5">
                <Sparkles size={12} /> Begin direct
              </div>
              <h2 className="text-white text-xl md:text-2xl font-bold tracking-[-0.01em]">Start staging</h2>
              <p className="text-white/85 text-sm mt-1">Kies een woning en upload je foto's. Klaar in een paar seconden.</p>
            </div>
            <span className="bg-white text-accent px-5 py-3 rounded-xl text-sm font-bold inline-flex items-center gap-2 shadow-md">
              Start nu <ArrowRight size={14} />
            </span>
          </div>
        </button>

        {/* Onboarding stappenplan */}
        <OnboardingChecklist onCreditsUpdate={onCreditsUpdate} onNavigate={onNavigate} />

        {/* Statistiek tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4">
          <StatTile
            icon={<ImageIcon size={16} />}
            label="Totaal renders"
            value={loading ? '-' : totalRenders.toLocaleString('nl-NL')}
            accent
          />
          <StatTile
            icon={<Home size={16} />}
            label="Woningen"
            value={loading ? '-' : String(propertyCount)}
            onClick={onNavigateProperties}
          />
          <StatTile
            icon={<Zap size={16} />}
            label="Credits"
            value={String(user.credits)}
            sub={user.credits < 10 ? 'Bijna op — bijkopen' : undefined}
            subWarning={user.credits < 10}
            onClick={user.credits < 10 ? onNavigateSubscription : undefined}
          />
          <StatTile
            icon={<Clock size={16} />}
            label="Laatste render"
            value={loading ? '-' : timeAgo(lastRenderAt)}
            small
          />
        </div>

        {/* Recente activiteit */}
        <div className="grid md:grid-cols-1 gap-4">
          <div className="rounded-2xl bg-white border border-black/8 p-5 md:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-ink">Recent</h3>
              <button
                onClick={onNavigateProperties}
                className="text-xs font-semibold text-accent hover:text-accent-hover transition-colors flex items-center gap-1"
              >
                Alle woningen <ArrowRight size={12} />
              </button>
            </div>
            {loading ? (
              <p className="text-sm text-ink/40 py-6 text-center">Laden…</p>
            ) : recentProperties.length === 0 ? (
              <div className="py-10 text-center">
                <p className="text-sm text-ink/50 mb-3">Nog geen renders. Klik hierboven op Start staging om te beginnen.</p>
                <button
                  onClick={() => setStartStagingOpen(true)}
                  className="inline-flex items-center gap-2 bg-accent text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-accent-hover transition-colors"
                >
                  Start staging <ArrowRight size={12} />
                </button>
              </div>
            ) : (
              <ul className="divide-y divide-black/6">
                {recentProperties.map(p => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => onSelectProperty?.(p)}
                      disabled={!onSelectProperty}
                      className="w-full py-3 flex items-center justify-between gap-3 text-left rounded-md px-2 -mx-2 hover:bg-black/[0.03] focus:outline-none focus:ring-2 focus:ring-accent/40 transition-colors disabled:cursor-default disabled:hover:bg-transparent"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-ink truncate">{p.address}</p>
                        <p className="text-xs text-ink/50 mt-0.5">{p.render_count} render{p.render_count === 1 ? '' : 's'}</p>
                      </div>
                      <span className="text-xs text-ink/45 shrink-0 inline-flex items-center gap-1">
                        {timeAgo(p.last_render_at)}
                        {onSelectProperty && <ArrowRight size={12} className="text-ink/30" />}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

        </div>

      </div>

      {/* STA-198: typeahead modal — kies bestaande woning of maak nieuwe + ga direct naar upload */}
      <StartStagingModal
        open={startStagingOpen}
        onClose={() => setStartStagingOpen(false)}
        onPick={(p) => (onStartStaging ?? onSelectProperty)?.(p)}
      />
    </div>
  );
};

interface StatTileProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  subWarning?: boolean;
  accent?: boolean;
  small?: boolean;
  onClick?: () => void;
}

const StatTile: React.FC<StatTileProps> = ({ icon, label, value, sub, subWarning, accent, small, onClick }) => (
  <div
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    className={`rounded-2xl border p-4 md:p-5 transition-colors ${accent ? 'bg-accent/5 border-accent/20' : 'bg-white border-black/8 hover:border-black/15'} ${onClick ? 'cursor-pointer hover:shadow-sm' : ''}`}
  >
    <div className={`inline-flex items-center justify-center w-8 h-8 rounded-lg mb-3 ${accent ? 'bg-accent text-white' : 'bg-ink/5 text-ink/60'}`}>
      {icon}
    </div>
    <p className="text-[10.5px] font-semibold uppercase tracking-[0.11em] text-ink/50 mb-1">{label}</p>
    <p className={`font-bold tracking-[-0.02em] text-ink ${small ? 'text-lg md:text-xl' : 'text-2xl md:text-3xl'}`}>{value}</p>
    {sub && (
      <p className={`text-[11px] font-semibold mt-1 ${subWarning ? 'text-red-600' : 'text-ink/50'}`}>{sub}</p>
    )}
  </div>
);

